import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { Grid } from "@mui/material";
import { generateGuid } from "core/utils/str";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { DatePicker } from "components/DatePicker";
import { Autocomplete } from "components/Autocomplete";
import { MultilineTextField } from "components/MultilineTextField";
import { useLazyFetchGetV2 } from "hooks/fetch/useLazyFetchGetV2";
import { styled } from "@mui/material/styles";
import { useFetchPostV2 } from "hooks/fetch/useFetchPostV2";
import { Dialog } from "components/Dialog";
import { useSitesQuery } from "hooks/queries/useSitesQuery";
import { START_OF_TODAY_DATE } from "core/date-helpers";

export default function NewBlankCaseDialog({
  open,
  setOpen,
  getUrlParams,
  downDerateTrackerSource
}) {
  const history = useHistory();

  const [caseDescription, setCaseDescription] = useState("");
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [caseOpenDate, setCaseOpenDate] = useState(new Date());
  const [validationErrors, setValidationErrors] = useState([]);
  const [machines, setMachines] = useState([]);
  const [components, setComponents] = useState([]);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const {
    isLoading: sitesLoading,
    isError: sitesError,
    data: sitesData
  } = useSitesQuery();
  const sites = useMemo(() => sitesData?.results ?? [], [sitesData]);

  const {
    loading: machinesLoading,
    error: machinesError,
    getCallback: getMachines
  } = useLazyFetchGetV2(`machines?size=10000&site_id=${selectedSite?.id}`);

  useEffect(() => {
    if (selectedSite) {
      getMachines().then(({ results }) => {
        setMachines(results);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite]);

  const {
    loading: componentsLoading,
    error: componentsError,
    getCallback: getComponents
  } = useLazyFetchGetV2(
    `components?size=10000&scalar_configuration_id__in=${selectedMachine?.scalar_configurations.join(
      ","
    )}`
  );

  useEffect(() => {
    if (selectedMachine) {
      getComponents().then(({ results }) => {
        setComponents(results);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMachine]);

  const handleCompleted = async response => {
    setOpen(false);
    addSnackbarToQueue({
      severity: "success",
      message: <SnackbarContent>Case created successfully.</SnackbarContent>,
      autoHideDuration: 6000
    });

    history.push({
      pathname: `case-management/${response.id}`,
      state: {
        search: getUrlParams ? getUrlParams().toString() : null,
        downDerateTrackerSource: downDerateTrackerSource
      }
    });
  };

  const {
    postCallback: createCase,
    loading: createCaseInProgress,
    error: createCaseError
  } = useFetchPostV2("cases");

  const handleSetSite = site => {
    setSelectedSite(site ?? null);
    setSelectedMachine(null);
    setSelectedComponent(null);
  };

  const handleSetMachine = machine => {
    setSelectedMachine(machine ?? null);
    setSelectedComponent(null);
  };

  const handleSetComponent = component => {
    setSelectedComponent(component);
  };

  const validateAndCreate = async () => {
    const newValidationErrors = [];

    if (!caseOpenDate) {
      newValidationErrors.push({
        field: "caseOpenDate",
        helperText: "Required"
      });
    }

    if (!selectedSite) {
      newValidationErrors.push({ field: "site", helperText: "Required" });
    }

    if (!selectedMachine) {
      newValidationErrors.push({
        field: "machine",
        helperText: "Required"
      });
    }

    if (!selectedComponent) {
      newValidationErrors.push({
        field: "component",
        helperText: "Required"
      });
    }

    if (!caseDescription) {
      newValidationErrors.push({
        field: "caseDescription",
        helperText: "Required"
      });
    }

    if (newValidationErrors.length === 0) {
      const tempCase = {
        name: generateGuid(),
        description: caseDescription,
        open_date: caseOpenDate.toISOString(),
        machine: selectedMachine.id,
        component: selectedComponent.id,
        estimated_saving: selectedComponent.maintenance_cost ?? 0,
        priority: 1,
        machine_status: 1,
        attributes: {}
      };
      try {
        const newCase = await createCase(tempCase);
        handleCompleted(newCase);
      } catch (error) {
        console.error(error);
        addSnackbarToQueue({
          message: "Case failed to be created.",
          severity: "error"
        });
      }
    } else {
      setValidationErrors(newValidationErrors);
    }
  };

  const showErrorText = (field, loadingError) => {
    const validationError = validationErrors?.find(
      error => error.field === field
    )?.helperText;

    if (validationError) return validationError;

    return loadingError ? "Failed to load" : "";
  };

  // Show an error (either validation or loading error)
  const isFieldValid = field => {
    return !validationErrors?.some(error => error.field === field);
  };

  const onClose = () => {
    clearSelection();
    setOpen(false);
  };

  // Clear previously selected options when cancelling - clean form when adding new case.
  const clearSelection = () => {
    setSelectedSite(null);
    setSelectedMachine(null);
    setSelectedComponent(null);
    setCaseDescription(null);

    // Reset errors array since we've cleared selection
    validationErrors.length = 0;
  };

  return (
    <>
      <Dialog
        dialogId="new-case-dialog"
        open={open}
        title="New Case"
        confirmText="Add"
        size="large"
        actionsDisabled={createCaseInProgress}
        errorText={createCaseError ? "Failed to load content..." : undefined}
        onConfirm={() => validateAndCreate()}
        onClose={onClose}
        aria-label="case creation dialog"
        data-qa="CP-Dialog-New-Case"
        allowClickaway
      >
        <Grid container direction="column" spacing={2}>
          <Grid container item spacing={2}>
            <Grid item xs={12} sm={4}>
              {/* TODO: investigate if there is a better solution than making the input readonly */}
              <DatePicker
                open={isDatePickerOpen}
                onOpen={() => setIsDatePickerOpen(true)}
                onClose={() => setIsDatePickerOpen(false)}
                slotProps={{
                  textField: {
                    inputProps: {
                      readOnly: true
                    },
                    onClick: () => {
                      setIsDatePickerOpen(true);
                    }
                  }
                }}
                onChange={date => {
                  setCaseOpenDate(date);
                }}
                maxDate={START_OF_TODAY_DATE}
                date={caseOpenDate}
                disableFuture={true}
                invalidDateMessage={""}
                ariaLabel="date time input field"
                label="Open Date *"
                data-qa="CP-Date-Picker-Case-Open-Date"
                size="medium"
              />
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                id="site-select"
                name="site_select_text"
                label="Site"
                data-qa="CP-Text-Site"
                required
                autoHighlight
                loading={sitesLoading}
                loadingText={"Loading..."}
                options={sites}
                getOptionLabel={option => option.name}
                value={selectedSite}
                onChange={(_event, site) => handleSetSite(site)}
                error={sitesError || !isFieldValid("site")}
                helperText={showErrorText("site", sitesError)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                id="machine-select"
                name="machine_select_text"
                label="Machine"
                data-qa="CP-Text-Machine"
                disabled={!selectedSite}
                error={machinesError || !isFieldValid("machine")}
                helperText={showErrorText("machine", machinesError)}
                required
                autoHighlight
                loading={machinesLoading}
                loadingText={"Loading..."}
                options={machines}
                getOptionLabel={option => option.name}
                value={selectedMachine}
                onChange={(_event, machine) => handleSetMachine(machine)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                id="component-select"
                name="component_select_text"
                label="Component"
                data-qa="CP-Text-Component"
                required
                disabled={!selectedMachine}
                error={componentsError || !isFieldValid("component")}
                helperText={showErrorText("component", componentsError)}
                autoHighlight
                loading={componentsLoading}
                loadingText={"Loading..."}
                options={components}
                getOptionLabel={component => component.name}
                value={selectedComponent}
                onChange={(_event, component) => handleSetComponent(component)}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <MultilineTextField
              id="case-description"
              value={caseDescription}
              label="Description"
              required
              fullWidth
              onChange={evt =>
                setCaseDescription(evt.target.value.replace(/^\s*/g, ""))
              }
              error={!isFieldValid("caseDescription")}
              helperText={showErrorText("caseDescription")}
              data-qa="CP-Text-Case-Description"
            />
          </Grid>
        </Grid>
      </Dialog>
    </>
  );
}

NewBlankCaseDialog.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  getUrlParams: PropTypes.func,
  downDerateTrackerSource: PropTypes.bool
};

const SnackbarContent = styled("div")`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(2)};
`;
